import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  Filter as FilterIcon,
  Grid2X2,
  List,
  Plus,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import MainContainer from '@/components/containers/MainContainer';
import Bar from '@/components/elements/Bar';
import Filter from '@/components/filter/Filter';
import { SearchContext } from '@/features/project/building/Building';
import { cn } from '@/utils/cn';
import { useAppearanceList, useColors, usePalette } from './api';
import { AppearancePreview } from './components/AppearancePreview';
import { AppearanceDetailPanel } from './components/AppearanceDetailPanel';
import { ColorDetailPanel } from './components/ColorDetailPanel';
import { PaletteItemDetailPanel } from './components/PaletteItemDetailPanel';
import { PaletteCatalogPicker } from './components/PaletteCatalogPicker';
import { AppearanceFormDialog } from './dialogs/AppearanceFormDialog';
import { ColorFormDialog } from './dialogs/ColorFormDialog';
import { LIBRARIES, LIBRARY_PALETTE } from './types';
import type { ActiveFilter, AppearanceDetail, Library, ModelColor, PaletteKind } from './types';

type Editor = { detail?: AppearanceDetail; duplicate?: boolean };
type ColorEditor = { initial?: ModelColor };

interface ListRow {
  id: string;
  title: string;
  code: string;
  hex?: string | null;
  imageUrl?: string | null;
  meta?: string;
  inactive: boolean;
  warning?: string;
}

const FILTER_VALUES = {
  is_active: [
    { value: 'true', title: 'Aktivní' },
    { value: 'false', title: 'Neaktivní' },
  ],
};

const EMPTY_LIBRARY: Record<Library, { title: string; description: string }> = {
  appearances: {
    title: 'Model zatím nemá žádné vzhledy',
    description: 'Vytvořte první vzhled z barvy, dezénu a úpravy povrchu.',
  },
  patterns: { title: 'Paleta je prázdná', description: 'Přidejte položku z katalogu řešení.' },
  colors: { title: 'Paleta barev je prázdná', description: 'Založte první barvu.' },
  finishes: { title: 'Paleta je prázdná', description: 'Přidejte položku z katalogu řešení.' },
};

function useDebounced<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
}

export default function AppearancesPage() {
  const modelId: string | null = useSelector((state: any) => state.contract?.modelId ?? null);
  const permission = useSelector((state: any) => state.auth?.pagePermission);
  const canEdit = permission === 2;
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const library: Library =
    LIBRARIES.find((item) => item.id === params.get('library'))?.id || 'appearances';
  const meta = LIBRARIES.find((item) => item.id === library);
  const isColors = library === 'colors';
  const paletteKind: PaletteKind | null =
    library === 'appearances' || library === 'colors' ? null : LIBRARY_PALETTE[library];

  const [searches, setSearches] = useState<Partial<Record<Library, string>>>({});
  const [filter, setFilter] = useState<Record<string, string[]>>({ is_active: ['true'] });
  const [grid, setGrid] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(!!params.get('item'));
  const [editor, setEditor] = useState<Editor | null>(null);
  const [colorEditor, setColorEditor] = useState<ColorEditor | null>(null);
  const [picker, setPicker] = useState<PaletteKind | null>(null);
  const focusTarget = useRef<HTMLElement | null>(null);
  const selectedActive = filter.is_active ?? [];
  const activeFilter: ActiveFilter =
    selectedActive.length === 1 ? (selectedActive[0] as ActiveFilter) : 'true,false';
  const search = searches[library] || '';
  const debouncedSearch = useDebounced(search);

  const appearances = useAppearanceList(library === 'appearances' ? modelId : null, {
    search: debouncedSearch,
    isActive: activeFilter,
  });
  const palette = usePalette(paletteKind ?? 'pattern', paletteKind ? modelId : null, {
    search: debouncedSearch,
    isActive: activeFilter,
  });
  const colors = useColors(isColors ? modelId : null, {
    search: debouncedSearch,
    isActive: activeFilter,
  });

  let rows: ListRow[];
  let loading: boolean;
  if (paletteKind) {
    rows = (palette.data ?? []).map((item) => ({
      id: item.id,
      title: item.title,
      code: item.code,
      imageUrl: item.classified_solution.image_url,
      meta: `${item.appearance_count ?? 0} vzhledů`,
      inactive: !item.is_active,
      warning: item.classified_solution.is_active ? undefined : 'Deaktivováno v katalogu',
    }));
    loading = palette.isLoading;
  } else if (isColors) {
    rows = (colors.data ?? []).map((item) => ({
      id: item.id,
      title: item.title,
      code: item.code,
      hex: item.hex,
      meta: `${item.appearance_count ?? 0} vzhledů`,
      inactive: !item.is_active,
    }));
    loading = colors.isLoading;
  } else {
    rows = appearances.items.map((item) => ({
      id: item.id,
      title: item.title,
      code: item.custom_title ? item.sys_title : '',
      hex: item.hex,
      inactive: !item.is_active,
    }));
    loading = appearances.isLoading;
  }
  const count = library === 'appearances' ? appearances.count : rows.length;
  const showCodePrefix = library !== 'appearances';
  const selectedId = params.get('item') || rows[0]?.id;

  // A different model has different appearances and palettes.
  const previousModel = useRef(modelId);
  useEffect(() => {
    if (previousModel.current === modelId) return;
    previousModel.current = modelId;
    const next = new URLSearchParams(location.search);
    next.delete('item');
    history.replace({ pathname: location.pathname, search: next.toString() });
    setEditor(null);
    setColorEditor(null);
    setPicker(null);
  }, [modelId]);

  const navigate = (nextLibrary: Library, id?: string) => {
    const next = new URLSearchParams(location.search);
    next.set('library', nextLibrary);
    if (id) next.set('item', id);
    else next.delete('item');
    history.push({ pathname: location.pathname, search: next.toString() });
    if (id) setSearches((old) => ({ ...old, [nextLibrary]: '' }));
    setShowMobileDetail(!!id);
  };
  const select = (id: string) => {
    const next = new URLSearchParams(location.search);
    next.set('library', library);
    next.set('item', id);
    history.replace({ pathname: location.pathname, search: next.toString() });
    setShowMobileDetail(true);
  };
  const openEditor = (value: Editor) => {
    focusTarget.current = document.activeElement as HTMLElement;
    setEditor(value);
  };
  const openColorEditor = (value: ColorEditor) => {
    focusTarget.current = document.activeElement as HTMLElement;
    setColorEditor(value);
  };
  const handleAdd = () => {
    if (paletteKind) setPicker(paletteKind);
    else if (isColors) openColorEditor({});
    else openEditor({});
  };
  const returnFocus = () => {
    if (focusTarget.current?.isConnected) focusTarget.current.focus();
  };

  if (!modelId) {
    return (
      <MainContainer>
        <EmptyState
          className="flex-1"
          title="Vyberte akci a model"
          description="Vzhledy a palety barev, dezénů a úprav povrchu patří ke konkrétnímu modelu."
        />
      </MainContainer>
    );
  }

  const renderList = () => {
    if (loading) return <SpinnerOverlay />;
    if (!rows.length) {
      if (search || activeFilter !== 'true') {
        return (
          <EmptyState
            title="Žádné odpovídající položky"
            description="Zkuste změnit hledání nebo filtr stavu."
            actionLabel="Zrušit filtry"
            onAction={() => {
              setSearches({ ...searches, [library]: '' });
              setFilter({ is_active: ['true'] });
            }}
          />
        );
      }
      return (
        <EmptyState
          title={EMPTY_LIBRARY[library].title}
          description={EMPTY_LIBRARY[library].description}
        />
      );
    }
    return (
      <>
        <div className={grid ? 'grid grid-cols-2 2xl:grid-cols-3 gap-2' : 'space-y-1'}>
          {rows.map((row) => {
            const active = row.id === selectedId;
            return (
              <button
                type="button"
                key={row.id}
                aria-pressed={active}
                onClick={() => select(row.id)}
                className={cn(
                  'w-full text-left rounded border transition-colors focus-visible:ring-2 focus-visible:ring-primary-500',
                  grid ? 'p-2' : 'flex items-center gap-3 p-3',
                  active
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-transparent hover:bg-neutral-50',
                  grid && !active && 'border-neutral-200',
                  row.inactive && 'opacity-60'
                )}
              >
                <AppearancePreview
                  hex={row.hex}
                  patternImageUrl={row.imageUrl}
                  label={row.title}
                  className={grid ? 'h-24 mb-3' : 'h-11 w-11 shrink-0'}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-1">
                    <p className="font-medium text-sm text-neutral-800 break-words">
                      {showCodePrefix && (
                        <span className="font-mono text-neutral-500">{row.code} </span>
                      )}
                      {row.title}
                    </p>
                    {active && <Check size={14} className="text-primary-600 shrink-0 mt-1" />}
                  </div>
                  {!showCodePrefix && row.code && (
                    <p className="font-mono text-[10px] text-neutral-500 mt-1">{row.code}</p>
                  )}
                  <p className="text-[11px] text-neutral-400 mt-1">
                    {[row.meta, row.inactive && 'Neaktivní'].filter(Boolean).join(' · ')}
                  </p>
                  {row.warning && <p className="text-[11px] text-amber-700">{row.warning}</p>}
                </div>
                {!grid && <ChevronRight size={14} className="text-neutral-400 shrink-0" />}
              </button>
            );
          })}
        </div>
        {!paletteKind && appearances.hasNextPage && (
          <div className="flex justify-center p-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={appearances.isFetchingNextPage}
              onClick={() => appearances.fetchNextPage()}
            >
              Načíst další
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderDetailPanel = () => {
    if (!selectedId) {
      return (
        <EmptyState title="Vyberte položku" description="Detail se zobrazí po výběru položky." />
      );
    }
    if (paletteKind) {
      return (
        <PaletteItemDetailPanel
          key={selectedId}
          kind={paletteKind}
          id={selectedId}
          modelId={modelId}
          canEdit={canEdit}
          onNavigate={navigate}
          onBack={() => setShowMobileDetail(false)}
        />
      );
    }
    if (isColors) {
      return (
        <ColorDetailPanel
          key={selectedId}
          id={selectedId}
          canEdit={canEdit}
          onEdit={(color) => openColorEditor({ initial: color })}
          onNavigate={navigate}
          onBack={() => setShowMobileDetail(false)}
        />
      );
    }
    return (
      <AppearanceDetailPanel
        key={selectedId}
        id={selectedId}
        canEdit={canEdit}
        onEdit={(detail) => openEditor({ detail })}
        onDuplicate={(detail) => openEditor({ detail, duplicate: true })}
        onNavigate={navigate}
        onBack={() => setShowMobileDetail(false)}
      />
    );
  };

  return (
    <MainContainer>
      <SearchContext.Provider value={{ filter, setFilter }}>
        <div className="flex flex-col min-h-0 min-w-0 flex-1 bg-white">
          <div className="flex-1 min-h-0 flex min-w-0">
            <section
              aria-label={meta.title}
              className={cn(
                'flex flex-col min-h-0 min-w-0 flex-1 lg:border-r border-neutral-150',
                grid ? 'lg:flex-[1.4]' : 'lg:max-w-md',
                showMobileDetail && selectedId && 'hidden lg:flex'
              )}
            >
              <Bar
                text={meta.title}
                count={count}
                actions={
                  <>
                    <Button
                      variant={!grid ? 'secondary' : 'ghost'}
                      size="icon-sm"
                      aria-label="Seznam"
                      aria-pressed={!grid}
                      onClick={() => setGrid(false)}
                    >
                      <List />
                    </Button>
                    <Button
                      variant={grid ? 'secondary' : 'ghost'}
                      size="icon-sm"
                      aria-label="Mřížka"
                      aria-pressed={grid}
                      onClick={() => setGrid(true)}
                    >
                      <Grid2X2 />
                    </Button>
                  </>
                }
                content={[
                  <Filter key="filter" filteredValues={FILTER_VALUES}>
                    <FilterIcon className="h-4 w-4" data-testid="appearances-filter-btn" />
                  </Filter>,
                  ...(canEdit
                    ? [
                        <Plus
                          key="add"
                          className="h-4 w-4"
                          data-testid="appearances-add-btn"
                          onClick={handleAdd}
                        />,
                      ]
                    : []),
                ]}
              />
              <div className="p-3 border-b border-neutral-150 bg-neutral-25/50">
                <div className="relative">
                  <Search
                    size={13}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"
                  />
                  <Input
                    className="h-8 pl-8 text-xs"
                    aria-label={`Hledat: ${meta.title}`}
                    placeholder="Vyhledat..."
                    value={search}
                    onChange={(event) => {
                      setSearches({ ...searches, [library]: event.target.value });
                      setShowMobileDetail(false);
                    }}
                  />
                </div>
              </div>
              <div className="relative flex-1 overflow-y-auto p-2">{renderList()}</div>
            </section>
            <div
              className={cn(
                'min-w-0 flex-1 lg:min-w-[320px]',
                showMobileDetail && selectedId ? 'block' : 'hidden lg:block'
              )}
            >
              {renderDetailPanel()}
            </div>
          </div>
          {colorEditor && (
            <ColorFormDialog
              modelId={modelId}
              initial={colorEditor.initial}
              onClose={() => setColorEditor(null)}
              onSaved={(id) => {
                setColorEditor(null);
                navigate('colors', id);
              }}
              returnFocus={returnFocus}
            />
          )}
          {editor && (
            <AppearanceFormDialog
              modelId={modelId}
              initial={editor.detail}
              duplicate={editor.duplicate}
              onClose={() => setEditor(null)}
              onSaved={(id) => {
                setEditor(null);
                navigate('appearances', id);
              }}
              returnFocus={returnFocus}
            />
          )}
          {picker && (
            <PaletteCatalogPicker
              kind={picker}
              modelId={modelId}
              onClose={() => setPicker(null)}
              onAdded={(item) => {
                setPicker(null);
                navigate(library, item.id);
              }}
            />
          )}
        </div>
      </SearchContext.Provider>
    </MainContainer>
  );
}
